<template>
    <ClientOnly>
        <section class="table-line"
            :style="splitStyle"
        >
            <div class="hands"
                v-if="hands2?.length"
                :style="handsStyle(hands2)"
                v-observe-height
            >
                <div class="forStatusBlock" :style="forStatusBlockStyle(hands2)" >
                    <Status v-if="!showResult"
                        :handsUserState="aStatus[1]" 
                        :winAmount="aWinAmount[1]"
                        :currency="round.currency"
                    />
                </div>
                <Card
                    v-for="(card, index) in hands2"
                    :key="`hand2-${index}`"
                    :card-name="card.cardID"
                    :card="card"
                    :status="aStatus[1]"
                    :style="{marginTop: `${index * 20}%`}" 
                    :animation="animation"
                />
                <Point
                    v-if="hands.length > 0"
                    :status="aStatus[1]"
                    :pointArr="getTotalPoints(hands2)"
                />
            </div>
            <!--
                ↑ 第 2 手牌（按需显示） 
                ↓ 第 1 手牌（常驻）此处也用于陈列庄家的牌  
            -->
            <div class="hands"
                :style="handsStyle(hands)"
                v-observe-height
            >
                <div class="forStatusBlock"
                    v-if="!dealer"
                    :style="forStatusBlockStyle(hands)"
                >
                    <Status
                        v-if="!showResult"
                        :handsUserState="aStatus[0]" 
                        :winAmount="aWinAmount[0]"
                        :currency="round.currency"
                    />
                    <!-- ↑ 金额信息 -->
                    <!-- ↓ 玩家前2张牌=21点时显示 -->
                    <img class="imgBlackjack"
                        v-if="isBlackjack"
                        :style="showResult ? {animation: 'none', width: '82%'} : {}"
                        src="@/pages/casino/blackjackGame/img/blackjack.webp"
                    />
                </div>
                <Card v-for="(card, index) in hands"
                    :key="index"
                    :card-name="card.cardID"
                    :card="card"
                    :style="{marginTop: `${index * 20}%`}"
                    :status="aStatus[0]"
                    :animation="animation"
                />
                <Point v-if="hands.length > 0"
                    :status="aStatus[0]"
                    :pointArr="getTotalPoints(hands)"
                />
            </div>
        </section>
    </ClientOnly>
</template>

<script lang="ts">
import {
    oMethods,
    type CardInterface,
    type userStatus,
    type RoundDataInterface,
} from '../ts/blackjackGame';
import Card from './Card.vue';
import Point from './Point.vue';
import Status from './Status.vue';
import useGlobalStore from '~/stores/global';


export default defineComponent({
    name: 'TableLine',
    components: {
        Card,
        Point,
        Status,
    },
    props: {
        dealer: Boolean, // 值为 true 表示是上方的庄家 
        hands: { // ← 庄家或玩家数据
            type: Array as PropType<CardInterface[]>,
            required: true,
            default: ()=>[],
        },
        hands2: { // ← 玩家数据
            type: Array as PropType<CardInterface[]>,
            required: false,
            default: ()=>[],
        },
        round: { // ← 玩家数据
            type: Object as PropType<RoundDataInterface>,
            default: ()=>({}),
        },
        handsUser1State: {
            type: String as PropType<userStatus>,
        },
        handsUser2State: {
            type: String as PropType<userStatus>,
        },
        animation: {
            type: Boolean,
            default: false,
        },
        showResult: Boolean, // 值为 true 表示用于窗口中显示游戏结果, 否则用于游戏页
    },
    data: () => {
        const animateConfig = {
            cardMoveDelay: 500,
            cardRotateDelay: 200,
            cardMoveDelayNoAnimate: 5,
        };
        return {
            animateConfig,
            isBlackjack: false,
            globalStore: useGlobalStore(),
            aCleaner: [],
        };
    },
    computed: {
        splitStyle() {
            const {hands, hands2, showResult, globalStore} = this;
            const hasSecondHand = hands2?.length > 0;
            const oBasicStyle = {
                // height: showResult ? '37.7%' : '28.2%',
                height: showResult ? '37.7%' : '32%',
            };
            if ([0, 2].includes(hands.length) && !hasSecondHand) {
                return oBasicStyle;
            }
            // console.log("计算样式： splitStyle(), this.hands:\n", deepCopy(this.hands));
            oBasicStyle['padding-right'] = (()=>{ 
                const aCondition = [
                    globalStore.innerWidth > 550,
                    !showResult,
                    hasSecondHand,
                ];
                if (aCondition.every(Boolean)) return '15%';
                return 0;
            })();
            // ↓ 双手牌专用样式  
            if (hasSecondHand) {
                Object.assign(oBasicStyle, {
                    'grid-template-columns': `50% 50%`, 
                    'grid-column': 'revert-layer', 
                });
            }
            return oBasicStyle;
        },
        // ↓ 赢钱金额
        aWinAmount(){
            const oDefault = {winAmount: 0};
            const {
                firstHand = oDefault,
                secondHand = oDefault,
                winAmount = 0, // ← 买保险后庄家前2张牌=21点后，玩家的赢钱金额只能在这取到
            } = this.round;
            return [
                firstHand.winAmount || winAmount || 0,
                secondHand.winAmount || 0,
            ];
        },
        // ↓ 计算状态
        aStatus(){
            const {
                dealer, 
                round,
                showResult,
                handsUser1State,
                handsUser2State,
            } = this;
            let aResult:userStatus[] = ['', ''];
            if (dealer) return aResult; // 庄家无状态 
            if (!showResult){
                aResult = [
                    handsUser1State || 'await',
                    handsUser2State || 'await',
                ];
                return aResult;
            }
            // ↓执行至此，说明现在是在窗口中显示游戏结果 
            // win, equal, lose, bust,
            return this.getStatus();
        },
    },
    watch: {
        'round.firstHand': {
            deep: true,
            immediate: true,
            handler(oNewHands01){
                if (import.meta.server || !oNewHands01) return;
                // console.log("发牌了：", deepCopy(oNewHands01.cards));  
                // const bOldStatus = !!this.isBlackjack; 
                this.isBlackjack = false;
                // ↓ 开局直接获胜的几个条件 
                const aConditions = [
                    !this.dealer,
                    !this?.hands2?.length, 
                    oNewHands01.points === 21,
                    oNewHands01.cards.length == 2,
                ];
                // if (import.meta.dev && '!this.dealer'){
                //     console.log("玩家得分：", oNewHands01.points);
                // }
                const hasGotBlackJack = aConditions.every(Boolean);
                this.blackjackVisibleChange(hasGotBlackJack);
            },
        },
    },
    directives:{
        observeHeight: {
            mounted(el) { 
                el._resizeObserver = new ResizeObserver((entries) => {
                    for (const entry of entries) {
                        const height = entry.target['offsetHeight']; // 获取最新高度（包含边框和滚动条）
                        entry.target['style'].setProperty('--the-hight',  `${height}px`);
                        // console.log('Height  updated:', height)
                    }
                });
                el._resizeObserver.observe(el); // 开始观察
            },
            beforeUnmount(el) {
                // 组件卸载时销毁观察器 
                if (el._resizeObserver) {
                    el._resizeObserver.disconnect();
                    delete el._resizeObserver;
                };
            },
        },
    },
    methods: {
        ...oMethods,
        // ↓ 用于游戏历史窗口 
        getStatus(){
            const { dealerHand, firstHand, secondHand, } = this.round;
            const iDealerPiont = dealerHand.points;
            const arr:userStatus[] = [
                (()=>{
                    if (!firstHand.winAmount) return 'lose'; 
                    if (firstHand.points === iDealerPiont) return 'equal'; 
                    return 'win';
                })(),
                (()=>{
                    if (!secondHand) return ''; 
                    if (!secondHand.winAmount) return 'lose'; 
                    if (secondHand.points === iDealerPiont) return 'equal'; 
                    return 'win';
                })(),
            ];
            return arr; 
        },
        // ↓ 计算卡片“容器” 的宽度等样式  
        handsStyle(hands) {
            let {length = 0} = hands || []; 
            if (!length) return {}; 
            let handsCount = this?.hands2?.length ? 2 : 1;
            const width = (()=>{
                // I = m(()=>(128 + [0, 12, 9][e.handsCount] * e.cards.length) / 610 * 100 * e.handsCount)
                const step01 = 128 + [0, 12, 9][handsCount] * length;
                const result = step01 / 610 * 100 * handsCount;
                return result + '%';
            })();
            const height = (()=>{
                // const o = [0, 3, 5][e.handsCount]; return 110 - e.cards.length * o;
                const step01 = [0, 3, 5][handsCount]; 
                const result = 110 - length * step01; 
                return result + '%';
            })();
            length++; // ← 此操作出自原版：原版有4张牌，最终结果 repeat(5, 20%); 
            const oResult = {
                width,
                height,
                gridTemplateColumns: `repeat(${length}, ${100 / length}%)`,
            };
            return oResult;
        },
        forStatusBlockStyle(hands) {
            return {
                width: `${hands.length * 6 + 99}.3px`,
                height: '118.2px',
            };
        },
        // ↓ 控制 blackjack 图标弹出或隐藏 
        async blackjackVisibleChange(hasGotBlackJack=false){ 
            if (!hasGotBlackJack) { 
                this.isBlackjack = false;
                return;
            }else if (this.showResult){ // 游戏历史窗口不需要动画
                this.isBlackjack = true;
                return;
            }
            let iDelay = this.animation ? 800 : 50;
            await Sleep(iDelay);
            this.isBlackjack = true;
            await Sleep(1800);
            this.isBlackjack = false;
        },
    },
});
</script>

<style lang="scss" scoped src="../style/blackjack-wrapper.scss"></style>
<style lang="scss" scoped src="../style/blackjack-card.scss"></style>

